import type { GoalId } from './quiz';

export interface CompetitorFeatureRow {
  feature: string;
  peech: boolean;
  opponent: boolean;
}

export interface CompetitorDefinition {
  name: string;
  matchers: string[];
}

type CompetitorClass = 'voiceover' | 'reader';
type FeatureId =
  | 'imports'
  | 'autoCleanToc'
  | 'stability'
  | 'instantListening'
  | 'unlimited'
  | 'adhdTools'
  | 'complexDocs'
  | 'librarySync';

export const COMPETITOR_KLASS: Record<string, CompetitorClass> = {
  ElevenLabs: 'voiceover',
  'Play.ht': 'voiceover',
  'Murf AI': 'voiceover',
  'Resemble AI': 'voiceover',
  'Amazon Polly': 'voiceover',
  'VEED.IO': 'voiceover',
  'Voice.ai': 'voiceover',
  Fliki: 'voiceover',

  Speechify: 'reader',
  'Natural Reader': 'reader',
  TextAloud: 'reader',
  Balabolka: 'reader',

  // Others in your list you may keep adding here:
  'Narrator AI': 'voiceover',
  Respeecher: 'voiceover',
  Voicify: 'voiceover',
};

const FEATURE_LABELS: Record<FeatureId, string> = {
  imports: $localize`:@@competitorFeatureImports:1-tap imports (web, PDFs, books)`,
  autoCleanToc: $localize`:@@competitorFeatureAutoCleanToc:Auto-clean reading mode & chapters`,
  stability: $localize`:@@competitorFeatureStability:Stable at 2–3× for hours`,
  instantListening: $localize`:@@competitorFeatureInstantListening:Instant listening`,
  unlimited: $localize`:@@competitorFeatureUnlimited:No daily caps (voice & listen)`,
  adhdTools: $localize`:@@competitorFeatureAdhdTools:ADHD & Dyslexia tools`,
  complexDocs: $localize`:@@competitorFeatureComplexDocs:Understands complex docs`,
  librarySync: $localize`:@@competitorFeatureLibrarySync:Library sync across devices`,
};

// Goal-priority lists (highest first). We use goal string values
const GOAL_FEATURE_ORDER: Record<string, FeatureId[]> = {
  listenOnTheGo: [
    'instantListening',
    'stability',
    'imports',
    'unlimited',
    'librarySync',
    'autoCleanToc',
  ],
  adhdEyeStrainDyslexia: [
    'adhdTools',
    'stability',
    'autoCleanToc',
    'imports',
    'complexDocs',
    'unlimited',
  ],
  learnSomethingNew: [
    'autoCleanToc',
    'stability',
    'imports',
    'complexDocs',
    'unlimited',
    'librarySync',
  ],
  beMoreProductive: [
    'imports',
    'autoCleanToc',
    'stability',
    'unlimited',
    'librarySync',
    'instantListening',
  ],
  makeReadingEasier: [
    'adhdTools',
    'autoCleanToc',
    'imports',
    'librarySync',
    'stability',
    'unlimited',
  ],
  everythingIsPerfect: [
    'imports',
    'autoCleanToc',
    'stability',
    'unlimited',
    'librarySync',
    'instantListening',
  ],
};

// Conservative, brand-specific overrides (optional, safe defaults if absent)
const COMPETITOR_SUPPORT_HINTS: Record<string, Partial<Record<FeatureId, boolean>>> = {
  // Reader app class (tend to have these)
  Speechify: {
    imports: true,
    librarySync: true,
    stability: true,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    instantListening: true,
  },
  'Natural Reader': {
    imports: true,
    librarySync: true,
    stability: true,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    instantListening: true,
  },
  TextAloud: {
    imports: true,
    librarySync: true,
    stability: true,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    instantListening: true,
  },
  Balabolka: {
    imports: true,
    librarySync: false,
    stability: true,
    unlimited: true,
    complexDocs: false,
    adhdTools: false,
    instantListening: false,
  },

  // Voiceover/studio class (optimize for production, not reading workflow)
  ElevenLabs: {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'Play.ht': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'Murf AI': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'Resemble AI': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'Amazon Polly': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'VEED.IO': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'Voice.ai': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  Fliki: {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  Respeecher: {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  'Narrator AI': {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
  Voicify: {
    imports: false,
    librarySync: false,
    instantListening: false,
    unlimited: false,
    complexDocs: false,
    adhdTools: false,
    stability: true,
  },
};

// Generic fallback profiles if competitor not detected
const DEFAULT_READER_HINTS: Partial<Record<FeatureId, boolean>> = {
  imports: true,
  librarySync: true,
  stability: true,
  unlimited: false,
  complexDocs: false,
  adhdTools: false,
  instantListening: true,
};
const DEFAULT_VOICEOVER_HINTS: Partial<Record<FeatureId, boolean>> = {
  imports: false,
  librarySync: false,
  stability: true,
  unlimited: false,
  complexDocs: false,
  adhdTools: false,
  instantListening: false,
};

export const COMPETITORS: CompetitorDefinition[] = [
  {
    name: 'ElevenLabs',
    matchers: ['elevenlabs', 'eleven labs', '11labs'],
  },
  {
    name: 'Speechify',
    matchers: ['speechify'],
  },
  {
    name: 'Play.ht',
    matchers: ['play.ht', 'play ht', 'playht'],
  },
  {
    name: 'Murf AI',
    matchers: ['murf ai', 'murfai', 'murf'],
  },
  {
    name: 'Natural Reader',
    matchers: ['natural reader', 'naturalreader'],
  },
  {
    name: 'Amazon Polly',
    matchers: ['amazon polly', 'polly'],
  },
  {
    name: 'VEED.IO',
    matchers: ['veed.io', 'veed'],
  },
  {
    name: 'Balabolka',
    matchers: ['balabolka'],
  },
  {
    name: 'Resemble AI',
    matchers: ['resemble ai', 'resemble.ai', 'resembleai'],
  },
  {
    name: 'Fliki',
    matchers: ['fliki'],
  },
  {
    name: 'Voicify',
    matchers: ['voicify'],
  },
  {
    name: 'TextAloud',
    matchers: ['textaloud', 'text aloud'],
  },
  {
    name: 'Respeecher',
    matchers: ['respeecher'],
  },
  {
    name: 'Narrator AI',
    matchers: ['narrator ai', 'narratorai'],
  },
  {
    name: 'Voice.ai',
    matchers: ['voice ai', 'voice.ai', 'voiceai'],
  },
];

export function buildDynamicRows(
  goalId: GoalId,
  competitorName?: string,
  maxRows: number = 6
): CompetitorFeatureRow[] {
  const order = GOAL_FEATURE_ORDER[goalId] ?? GOAL_FEATURE_ORDER['beMoreProductive'];
  const klass = (competitorName && COMPETITOR_KLASS[competitorName]) || 'reader';
  const hints =
    (competitorName && COMPETITOR_SUPPORT_HINTS[competitorName]) ||
    (klass === 'reader' ? DEFAULT_READER_HINTS : DEFAULT_VOICEOVER_HINTS);

  const selected = order.slice(0, maxRows);

  return selected.map(fid => ({
    feature: FEATURE_LABELS[fid],
    peech: true,
    opponent: !!hints[fid],
  }));
}
